import React, { FormEvent, useEffect, useState } from "react";
import { IOwnerPropertyOption, ISubAccount, ISubAccountFormValues } from "./types";
import { validateSubAccountForm } from "./utils/validateSubAccountForm";
import { resolveApiFieldErrors } from "./utils/resolveApiErrorMessage";
import PropertyMultiSelect from "./PropertyMultiSelect";
import SubAccountFormFields from "./SubAccountFormFields";

interface SubAccountFormProps {
  mode: "create" | "edit";
  initialAccount?: ISubAccount | null;
  properties: IOwnerPropertyOption[];
  isSubmitting: boolean;
  onSubmit: (values: ISubAccountFormValues) => Promise<void>;
  onCancel: () => void;
}

const EMPTY_VALUES: ISubAccountFormValues = {
  first_name: "",
  email: "",
  phone: "",
  password: "",
  password_confirmation: "",
  property_ids: [],
  assign_all_properties: false,
  active: true,
};

function mapAccountToForm(
  account: ISubAccount | null | undefined,
  properties: IOwnerPropertyOption[]
): ISubAccountFormValues {
  if (!account) {
    return EMPTY_VALUES;
  }

  const hasAllProperties =
    properties.length > 0 &&
    properties.every((property) => account.property_ids.includes(property.id));

  return {
    first_name: account.first_name,
    email: account.email,
    phone: account.phone,
    password: "",
    password_confirmation: "",
    property_ids: account.property_ids,
    assign_all_properties: hasAllProperties,
    active: account.active,
  };
}

export default function SubAccountForm({
  mode,
  initialAccount,
  properties,
  isSubmitting,
  onSubmit,
  onCancel,
}: SubAccountFormProps) {
  const [values, setValues] = useState<ISubAccountFormValues>(
    mapAccountToForm(initialAccount, properties)
  );
  const [errors, setErrors] = useState<
    Partial<Record<keyof ISubAccountFormValues, string>>
  >({});

  const lockIdentityFields =
    mode === "edit" && initialAccount?.created_for_sub_login === false;

  useEffect(() => {
    setValues(mapAccountToForm(initialAccount, properties));
    setErrors({});
  }, [initialAccount, mode, properties]);

  function updateField<K extends keyof ISubAccountFormValues>(
    key: K,
    value: ISubAccountFormValues[K]
  ) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const validationErrors = await validateSubAccountForm(values, mode, {
      lockIdentityFields,
    });
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    try {
      await onSubmit(values);
    } catch (error) {
      const apiFieldErrors = resolveApiFieldErrors(error);
      if (Object.keys(apiFieldErrors).length > 0) {
        setErrors((prev) => ({
          ...prev,
          ...apiFieldErrors,
        }));
      }
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4 rounded-2xl border border-[#E2E8F0] bg-white p-4 shadow-sm sm:p-6"
    >
      <div>
        <h2 className="text-lg font-bold text-[#2D3748]">
          {mode === "create" ? "Novo acesso" : "Editar acesso"}
        </h2>
        <p className="mt-1 text-sm text-[#64748B]">
          {lockIdentityFields
            ? "Atualize o status e os imóveis que este acesso poderá gerenciar."
            : "Cadastre e-mail, senha e os imóveis que este login poderá gerenciar."}
        </p>
      </div>

      <SubAccountFormFields
        values={values}
        errors={errors}
        mode={mode}
        lockIdentityFields={lockIdentityFields}
        disabled={isSubmitting}
        onChange={updateField}
      />

      <div className="space-y-1.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">
          Imóveis com acesso
        </span>
        <PropertyMultiSelect
          properties={properties}
          selectedIds={values.property_ids}
          assignAll={values.assign_all_properties}
          onSelectionChange={({ assignAll, propertyIds }) =>
            setValues((prev) => ({
              ...prev,
              assign_all_properties: assignAll,
              property_ids: propertyIds,
            }))
          }
          error={errors.property_ids}
          disabled={isSubmitting}
        />
      </div>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-full border border-[#CBD5E1] px-5 py-2.5 text-sm font-semibold text-[#475569] transition-colors hover:bg-[#F8FAFC] disabled:opacity-60"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-full bg-[#DD6435] px-5 py-2.5 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          {isSubmitting
            ? "Salvando..."
            : mode === "create"
              ? "Criar acesso"
              : "Salvar alterações"}
        </button>
      </div>
    </form>
  );
}
